import { useRef, useState } from 'react';
import { Download, FileText, Trash2, UploadCloud } from 'lucide-react';
import {
  COMPANY_DOCUMENT_CATEGORIES,
  requestCompanyDocumentUploadUrl,
  type CompanyDocument,
  type CompanyDocumentCategory,
} from '../../lib/companiesApi';
import {
  useCompanyDocuments,
  useCreateCompanyDocument,
  useDeleteCompanyDocument,
} from '../../hooks/useCompanies';
import { uploadToSignedUrl } from '../../lib/clientsApi';
import { useUploadQueue } from '../../hooks/useUploadQueue';
import { UploadProgressList } from '../ui/UploadProgress';
import { useConfirm } from '../ui/ConfirmProvider';
import { Select } from '../ui/Select';
import { formatFileSize } from '../chat/ChatFileCard';

// Company documents card — mounted on the company detail page
// (/settings/companies/:id), under the identity form. The firm's own papers
// (certificat de înregistrare, certificat constatator, statut / act
// constitutiv, anything else) live here in one place, and Firmio AI hands them
// out in chat on request ("dă-mi certificatul de înregistrare").

export const COMPANY_DOCUMENT_LABELS: Record<CompanyDocumentCategory, string> = {
  REGISTRATION_CERTIFICATE: 'Certificat de înregistrare',
  CONSTATATOR: 'Certificat constatator',
  STATUT: 'Statut / Act constitutiv',
  OTHER: 'Alt document',
};

export function CompanyDocumentsCard({
  companyId,
  canManage,
}: {
  companyId: string;
  canManage: boolean;
}) {
  const { data: documents, isLoading, isError } = useCompanyDocuments(companyId);
  const createDocument = useCreateCompanyDocument(companyId);
  const deleteDocument = useDeleteCompanyDocument(companyId);
  const confirm = useConfirm();
  const { uploads, isUploading, run } = useUploadQueue();

  const [category, setCategory] = useState<CompanyDocumentCategory>('REGISTRATION_CERTIFICATE');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setError(null);
    try {
      await run(file, async (onProgress) => {
        const contentType = file.type || 'application/octet-stream';
        const { uploadUrl, key } = await requestCompanyDocumentUploadUrl(companyId, {
          fileName: file.name,
          category,
          contentType,
          fileSize: file.size,
        });
        await uploadToSignedUrl(uploadUrl, file, contentType, onProgress);
        await createDocument.mutateAsync({
          name: file.name,
          category,
          s3Key: key,
          fileType: (file.name.split('.').pop() ?? '').toUpperCase() || undefined,
          fileSize: file.size,
        });
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Încărcarea a eșuat.');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function handleDelete(doc: CompanyDocument) {
    if (
      !(await confirm({
        message: `Ștergi documentul „${doc.name}”? Fișierul este eliminat și din stocare.`,
        danger: true,
      }))
    )
      return;
    setError(null);
    try {
      await deleteDocument.mutateAsync(doc.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ștergerea a eșuat.');
    }
  }

  const grouped = COMPANY_DOCUMENT_CATEGORIES.map((c) => ({
    category: c,
    items: (documents ?? []).filter((d) => d.category === c),
  })).filter((g) => g.items.length > 0);
  const uncategorised = (documents ?? []).filter(
    (d) => !(COMPANY_DOCUMENT_CATEGORIES as readonly string[]).includes(d.category),
  );

  return (
    <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Documentele firmei</h2>
          <p className="mt-1 text-sm text-gray-500">
            Certificat de înregistrare, certificat constatator, statut. Firmio AI ți le dă direct în
            chat când i le ceri.
          </p>
        </div>
      </div>

      {canManage && (
        <div className="mt-4 space-y-3">
          <div className="max-w-xs">
            <Select
              label="Tipul documentului încărcat"
              value={category}
              onChange={(v) => setCategory(v as CompanyDocumentCategory)}
              options={COMPANY_DOCUMENT_CATEGORIES.map((c) => ({
                value: c,
                label: COMPANY_DOCUMENT_LABELS[c],
              }))}
            />
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void handleFile(file);
            }}
            disabled={isUploading}
            className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-5 text-center transition-colors disabled:opacity-50 ${
              dragActive
                ? 'border-brand-500 bg-brand-50'
                : 'border-gray-300 bg-gray-50 hover:border-brand-400'
            }`}
          >
            <UploadCloud className="h-6 w-6 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              Trage un fișier aici sau apasă pentru a răsfoi
            </span>
            <span className="text-xs text-gray-400">
              Salvat ca {COMPANY_DOCUMENT_LABELS[category]} · PDF, DOCX, imagini, max. 25 MB
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <UploadProgressList uploads={uploads} />
        </div>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-4">
        {isLoading && <p className="text-sm text-gray-500">Se încarcă documentele…</p>}
        {isError && <p className="text-sm text-red-600">Documentele nu au putut fi încărcate.</p>}
        {!isLoading && !isError && (documents?.length ?? 0) === 0 && (
          <p className="text-sm text-gray-400">
            Niciun document încă.{canManage ? ' Încarcă mai sus certificatul de înregistrare.' : ''}
          </p>
        )}
        {[
          ...grouped,
          ...(uncategorised.length ? [{ category: 'OTHER' as const, items: uncategorised }] : []),
        ].map((group, idx) => (
          <div key={`${group.category}-${idx}`} className="mt-3 first:mt-0">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {COMPANY_DOCUMENT_LABELS[group.category]}
            </h3>
            <ul className="mt-1.5 divide-y divide-gray-100 rounded-md border border-gray-200">
              {group.items.map((doc) => (
                <li key={doc.id} className="flex items-center gap-3 px-3 py-2">
                  <FileText className="h-5 w-5 shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900" title={doc.name}>
                      {doc.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {[
                        doc.fileType?.toUpperCase(),
                        formatFileSize(doc.fileSize),
                        new Date(doc.uploadedAt).toLocaleDateString('ro-RO'),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  {doc.downloadUrl && (
                    <a
                      href={doc.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Download size={14} /> Descarcă
                    </a>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => void handleDelete(doc)}
                      disabled={deleteDocument.isPending}
                      className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      aria-label={`Șterge ${doc.name}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
